import { FC, MutableRefObject, useMemo } from "react";
import { List, Spin, Switch, ConfigProvider, Empty } from "antd";
import { getDisciplineDisplayIcon, getDisciplineIcon } from "Utils/ObraUtils";
import { Icon } from "Components/UI";
import EmptySearch from "Components/UI/SearchViewerInput/EmptySearch";
import HighlighterWords from "Components/UI/HighlightWords";
import { IModelsTree } from "Data/interfaces/FederatedViewer/IModelsTree";
import { IDisciplineViewerList } from "Data/interfaces/FederatedViewer/IDisciplineViewerList";
import InfoRender from "Components/UI/InfoRender";
import { IHideNodes, ISelectedNode } from "Data/interfaces/FederatedViewer/INodes";
import OtherActionsFile from "./components/OtherActionsFile";
import { ApisEnum } from "Data/enums/Apis";
import { fileDataSizeToBytes } from "Utils/toolBox";
import { Dispatch } from "redux";
import { FederatedViewerActions } from "Store/FederatedViewer/FederatedViewer.actions";
import { IUpdateStatusInViewer } from "Data/interfaces/ListagemArquivos/IUpdateStatusInViewer";
import { IAutodeskViewer } from "Data/interfaces/Viewer/IAutodeskViewer";
import { BucketFileStatusEnum, IFileData } from "Data/interfaces/ListagemArquivos/IListFilesResponse";
import { COORDLY_3D_VIEWER_EXTENSION } from "Utils/ViewerUtils";
import { ViewerEngineEnum } from "Data/enums/ViewerEngine";
import { useQueryRenderViewerFile } from "Hooks/files/useQueryRenderViewerFile";
import { autodeskSessionUrn, autodeskStatusOfFile, ifcUrnForFile, isNativeReadyFile } from "../../federationUrn";
import { BimSelection, BimViewerHandle } from "Data/interfaces/BimViewer/IBimViewerPort";
import IfcModelTree, { IIfcModelTreeEntry } from "../IfcModelTree";
import Ellipsis from "Components/UI/Ellipsis";
import AccessPermission from "Utils/AcessPermission";
import { Mixpanel } from "Utils/MixPanel";
import { LoadingOutlined } from "@ant-design/icons";
import { IUserInforResponse } from "Data/interfaces/Auth/IUserInfoResponse";
import { useSelector } from "react-redux";
import { getCurrentTenant, getTenants } from "Store/Tenant/Tenant.selector";
import VirtualTreeView from "./components/VirtualTreeView";
import styles from './Models.module.scss';

// Constante de módulo, não `|| []` no JSX: um literal novo a cada render entra
// como prop nova no `VirtualTreeView` e invalida os memos que dependem dela —
// justamente no caso comum, o do modelo que ainda não teve nada oculto.
const EMPTY_NODES: number[] = [];

interface IModels {
  activeDisciplineId: number | null;
  openDiscipline: (disciplineId: number) => void;
  backDisciplines: () => void;
  disciplineList: IDisciplineViewerList[];
  childrenDisciplinesActive?: IFileData[];
  searchDiscipline: string;
  searchDisciplineFiles: string;
  loadUrn: string | null;
  /** Devolve `false` quando a página recusa a ativação (mistura de motores). */
  onActiveUrns: (urn: string, extension: string) => boolean;
  accessViewerTab?: number | boolean;
  urns?: string[];
  onCTAMultipleRender: (index: number) => void;
  modelsTree?: IModelsTree[];
  onToggleNode: (node: number, urn: string) => void;
  hideNodes: IHideNodes[];
  onToggleSelectNode: (node: number, urn: string) => void;
  selectedNode: ISelectedNode | null;
  constructionSiteId: number;
  api?: ApisEnum;
  userInfo?: IUserInforResponse;
  ifcEngineReady?: boolean;
  /** Conversão Autodesk recusada pelo backend por falta de cota. */
  onLimitedResources?: () => void;
  /** Motor da sessão, decidido pelo 1º urn ativo. `null` = cena vazia. */
  sessionEngine?: 'ifc' | 'autodesk' | 'pending' | null;
  ifcLoadingUrn?: string | null;
  /** Árvore espacial por urn, vinda do motor IFC (uma por modelo na cena). */
  ifcTrees?: Record<string, IIfcModelTreeEntry>;
  ifcHandleRef?: MutableRefObject<BimViewerHandle | null>;
  /** Seleção da cena IFC — cada árvore assina para acompanhar o 3D. */
  ifcSelectionSubscribe?: (listener: (selection: BimSelection) => void) => () => void;
  dispatch: Dispatch;
}

const Models: FC<IModels> = ({
  activeDisciplineId,
  openDiscipline,
  backDisciplines,
  disciplineList,
  childrenDisciplinesActive,
  searchDiscipline,
  searchDisciplineFiles,
  loadUrn,
  onActiveUrns,
  accessViewerTab,
  urns,
  onCTAMultipleRender,
  modelsTree,
  onToggleNode,
  hideNodes,
  onToggleSelectNode,
  selectedNode,
  constructionSiteId,
  api,
  userInfo,
  ifcEngineReady,
  onLimitedResources,
  sessionEngine,
  ifcLoadingUrn,
  ifcTrees,
  ifcHandleRef,
  ifcSelectionSubscribe,
  dispatch,
}) => {
  const tenants = useSelector(getTenants);
  const currentTenant = useSelector(getCurrentTenant);
  const { mutate: renderViewerFile } = useQueryRenderViewerFile();

/**
 * O tamanho do arquivo na lista de modelos.
 *
 * `/Viewer/ListFiles` devolve o tamanho em `FileInfo.Size`, não em `SizeInBytes`
 * do topo — tanto que o `updateStatusInViewer` faz esse mapeamento na mão
 * (`FederatedViewer.sagas.ts`). Sem ele a lista lia um campo nulo, e `null`
 * dividido por qualquer coisa é 0: daí o "0.0 MB" em todo arquivo.
 *
 * Lê das duas fontes porque o mesmo componente recebe arquivo vindo do
 * `listFiles` e do `updateStatusInViewer`, que preenchem lugares diferentes.
 */
const fileSizeLabel = (item: IFileData): string => {
  const bytes = fileDataSizeToBytes(item);

  if (bytes <= 0) { return '-'; }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

  const antIcon = <LoadingOutlined style={{ color: '#3547C8', fontSize: 14 }} spin />;

  const activeDiscipline = useMemo(() => {
    if (activeDisciplineId) {
      return disciplineList.find(discipline => discipline.ConstructionSiteDisciplinesId === activeDisciplineId);
    }
    return undefined;
  }, [activeDisciplineId, disciplineList]);

  const handleUrns = (urn: string, extension: string) => {
    // A página é quem decide se o urn entra na sessão (motores não se misturam).
    // Recusado, o clique não é render: nada de Mixpanel — e, principalmente,
    // nada de `loadUrn`, que só o InternalViewer limpa e que, preso, desabilita
    // todos os switches, inclusive o de desligar o modelo já ativo.
    if (!onActiveUrns(urn, extension)) { return; }

    Mixpanel.track({
      name: 'RENDER_FILE',
      props: {
        constructionSiteId: Number(constructionSiteId),
        api: api,
        extension: extension,
        federatedViewer: true,
      },
      userInfo,
      currentListTenant: currentTenant,
      tenantIds: tenants && tenants.length > 0
        ? tenants?.map((tenant) => tenant?.TenantId).filter((tenantId) => tenantId !== undefined) as number[]
        : undefined,
    });
  }

  const iconLeft = (discipline: any) => {
    if (discipline && typeof discipline === 'object') {
      return getDisciplineDisplayIcon(discipline);
    }

    return getDisciplineIcon(String(discipline || ''));
  };

  // IFC servido pelo nosso motor não depende do artefato Autodesk: a identidade é
  // a urn sintética e o arquivo nunca entra na fila de conversão.
  //
  // Só vale quando a sessão NÃO é da Autodesk. Numa que começou por outro tipo de
  // arquivo, o IFC entra pelo caminho antigo — precisa da urn convertida, e com
  // ela do mesmo teste de status dos demais. Sem essa ressalva, o switch de um
  // IFC ainda não convertido ficaria habilitado numa sessão que não tem como
  // abri-lo: a urn sintética não diz nada para a Autodesk.
  const isSelfServedIfc = (file: IFileData) => (
    !!ifcEngineReady
    && sessionEngine !== 'autodesk'
    && file.Extension?.toLowerCase() === COORDLY_3D_VIEWER_EXTENSION
  );

  const switchUrn = (file: IFileData) => (
    isSelfServedIfc(file) ? ifcUrnForFile(file) : autodeskSessionUrn(file)
  );

  const isSwitchDisabled = (file: IFileData) => {
    if (!switchUrn(file) || typeof loadUrn === 'string') { return true; }
    // Um modelo por vez também no motor IFC. Ligar vários de uma vez põe
    // downloads e parses para competir, e ligar/desligar durante a carga só
    // surtia efeito quando ela terminava — a cena aplicava a última ação que
    // tivesse sobrado, sem relação com o que a pessoa acabara de clicar.
    if (ifcLoadingUrn) { return true; }
    // IFC no nosso motor liga só com o render nativo pronto: em conversão (pedida
    // sozinha ao abrir a página) fica desabilitado. O que já está ligado sempre
    // pode sair da sessão.
    if (isSelfServedIfc(file)) {
      return !urns?.includes(switchUrn(file)) && !isNativeReadyFile(file);
    }
    // Desligar um modelo já ativo nunca depende do status: se ele entrou na
    // sessão, precisa poder sair dela.
    if (urns?.includes(switchUrn(file))) { return false; }
    // Só o artefato pronto pode ser federado. Antes o teste era pelo negativo
    // (bloqueia Rendering/Error), então `NotRendered` e status ausente passavam:
    // o switch ligava, a Autodesk não achava o modelo e a sessão inteira travava.
    // IFC só convertido no nativo não tem o que abrir aqui.
    return autodeskStatusOfFile(file) !== BucketFileStatusEnum.Rendered;
  };

  const renderEmpty = () => {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
  }

  const handleUnbindFile = (file: IFileData) => {
    if (activeDiscipline) {
      const urnId = switchUrn(file) || null;
      if (urnId && urns?.includes(urnId)) {
        handleUrns(urnId, file.Extension)
      }
      const updateStatusInViewer: IUpdateStatusInViewer = {
        fileApiId: file.Identifier.ApiId || file.Identifier.ItemId,
        constructionSiteId: constructionSiteId,
        constructionSiteDisciplineId: activeDiscipline.ConstructionSiteDisciplinesId,
        api,
        inViewer: false,
        discipline: activeDiscipline,
        currentFile: file,
      };
      dispatch(FederatedViewerActions.updateStatusInViewer(updateStatusInViewer));
    }
  }

  const handleReRenderFileError = (file: IFileData) => {
    const request: IAutodeskViewer = {
      constructionSiteId: constructionSiteId,
      folderId: file.FileInfo.ApiFolderId || '',
      fileApiId: file.Identifier.ApiId || file.Identifier.ItemId,
    };

    const status = file.FileInfo.BucketFile?.Status;
    if (status !== 2 && status !== 3) { return; }

    dispatch(FederatedViewerActions.setFileStatusReRendering(request));

    // Com o nosso motor utilizável (flag + WebGPU) a nova tentativa é na nossa
    // conversão; sem WebGPU, Autodesk. O progresso volta pelo mesmo hub.
    if (ifcEngineReady) {
      renderViewerFile({
        constructionSiteId,
        fileApiId: request.fileApiId,
        engine: ViewerEngineEnum.Native,
      });
      return;
    }

    dispatch(FederatedViewerActions.autoDeskViewer({ ...request, extension: file.Extension, onLimitedResources }));
  };

  return (
    <ConfigProvider renderEmpty={() => renderEmpty()}>
      {(activeDisciplineId && activeDiscipline) ? (
        <div className={styles['listFilesWrapper']}>
          <List.Item
            className={styles['headerListFiles']}
          >
            <div className={styles['headerListFilesContent']}>
              <button
                onClick={() => backDisciplines()}
                className={styles['iconBack']}
              >
                <Icon icon="retroceder" customSize={14} customColor="#BEC2C6" />
              </button>
              <Icon
                icon={iconLeft(activeDiscipline)}
                customSize={18}
                className={styles['']}
                customColor="#BEC2C6"
              />
              <Ellipsis
                maxLength={32}
                phrase={activeDiscipline.CustomName || activeDiscipline.Discipline.Name}
                className={styles['disciplineHeader']}
              />
            </div>
          </List.Item>

          {searchDisciplineFiles && !childrenDisciplinesActive ? (
            <EmptySearch
              searchDisciplineFiles={searchDisciplineFiles}
            />
          ) : (
            <List
              className={styles['contentListFiles']}
              itemLayout="horizontal"
              dataSource={childrenDisciplinesActive}
              split={false}
              renderItem={(item, index) => {
                const modelExist = modelsTree?.find(model => model.urn.replace('urn:', '') === item.FileInfo.BucketFile?.Urn);
                const hideNodesExist = hideNodes.find(nodes => nodes.urn === item.FileInfo.BucketFile?.Urn);
                const selectedNodeExist = selectedNode?.urn === item.FileInfo.BucketFile?.Urn ? selectedNode?.nodeId : null;
                const ifcTreeExist = ifcTrees?.[switchUrn(item)];

                return (
                  <>
                    <List.Item className={styles['rowFile']}>
                      <div className={styles['leftRowFile']}>
                        <div className={styles['headerRowFile']}>
                          <HighlighterWords
                            text={item.Name}
                            maxLength={32}
                            wordSearched={searchDisciplineFiles}
                            className={styles['disciplineListText']}
                          />
                        </div>
                        <div className={styles['footerRowFile']}>
                          {/* <Icon icon="arquivar" customSize={9} customColor="#324150" className={styles['disciplineListIcon']} /> */}
                          <span className={styles['disciplineListText noBorder']}>
                            <InfoRender
                              status={item.FileInfo.BucketFile?.Status}
                              signalRender={item.RenderingSignalR}
                            />
                          </span>
                          <Ellipsis
                            maxLength={10}
                            phrase={`Versão ${item.Revision || '-'}`}
                            className={styles['disciplineListText']}
                          />
                          <span className={styles['disciplineListText']}>
                            {fileSizeLabel(item)}
                          </span>
                        </div>
                      </div>
                      {false && (<Spin indicator={antIcon} style={{ marginRight: '8px' }} />)}
                      {(accessViewerTab || (((!accessViewerTab || (accessViewerTab === 0))) && (urns && urns.length < 1))) && (
                        <Switch
                          size="small"
                          onChange={() => handleUrns(switchUrn(item), item.Extension)}
                          checked={urns?.includes(switchUrn(item))}
                          disabled={isSwitchDisabled(item)}
                        />
                      )}
                      {((!accessViewerTab || (accessViewerTab === 0))) && (urns && urns.length >= 1) && (
                        <Switch
                          size="small"
                          onClick={() => urns?.includes(switchUrn(item)) ? handleUrns(switchUrn(item), item.Extension) : onCTAMultipleRender(index)}
                          checked={urns?.includes(switchUrn(item)) ? true : false}
                          disabled={isSwitchDisabled(item)}
                        />
                      )}
                      {(((AccessPermission.isAdmin() || AccessPermission.isCoordenador()) && item.FileInfo.BucketFile?.Status === 1) || item.FileInfo.BucketFile?.Status !== 1) && (
                        <OtherActionsFile
                          file={item}
                          disabled={typeof loadUrn === 'string'}
                          onUnbindFile={handleUnbindFile}
                          onReRenderFileError={handleReRenderFileError}
                        />
                      )}
                    </List.Item>
                    {modelExist && (
                      <List.Item className={styles['rowTree']}>
                        <VirtualTreeView
                          result={modelExist}
                          onToggleNode={onToggleNode}
                          hideNodes={hideNodesExist?.hideNodes || EMPTY_NODES}
                          showNodes={hideNodesExist?.showNodes || EMPTY_NODES}
                          onToggleSelectNode={onToggleSelectNode}
                          selectedNode={selectedNodeExist}
                        />
                      </List.Item>
                    )}
                    {/* Árvore do motor IFC. Nunca concorre com a de cima: aquela
                        depende de `modelsTree`, que só existe para modelo da
                        Autodesk (casado por `BucketFile.Urn`). */}
                    {ifcTreeExist && ifcHandleRef && (
                      <List.Item className={styles['rowTree']}>
                        <IfcModelTree
                          tree={ifcTreeExist.tree}
                          modelIndex={ifcTreeExist.modelIndex}
                          handleRef={ifcHandleRef}
                          search={searchDisciplineFiles}
                          subscribeSelection={ifcSelectionSubscribe}
                        />
                      </List.Item>
                    )}
                  </>
                )
              }}
            />
          )}
        </div>
      ) : (
        searchDiscipline && !disciplineList.length ? (
          <EmptySearch
            searchDiscipline={searchDiscipline}
          />
        ) : (
          <div className={styles['disciplinesWrapper']}>
            <List
              itemLayout="horizontal"
              dataSource={disciplineList}
              split={false}
              renderItem={item => (
                <List.Item
                  className={styles['rowDisciplinesList']}
                  onClick={() => openDiscipline(item.ConstructionSiteDisciplinesId)}
                >
                  <div className={styles['leftRowDisciplinesList']}>
                    <Icon
                      icon={iconLeft(item)}
                      customSize={18}
                      className={styles['']}
                      customColor="#BEC2C6"
                    />
                    <HighlighterWords
                      text={item.CustomName || item.Discipline.Name}
                      maxLength={28}
                      wordSearched={searchDiscipline}
                      className={styles['disciplineListText']}
                    />
                  </div>
                  <Icon icon="retroceder" customSize={8} className={styles['iconExpanded']} color="cinzaSuperior" />
                </List.Item>
              )}
            />
          </div>
        )
      )}
    </ConfigProvider>
  )
}

export default Models;
